import React from 'react';
import { BasicSetupForm, IForm, KeyType } from './EmbedClaimPortal';
import ImageUploader from './ImageUploader';
import NumberInput from './NumberInput';
import TextInput from './TextInput';
import ColorPicker from './ColorPicker';
import { Button } from 'components/Button';
import SelectBox from './SelectBox';

interface ICustomizeSettingsProps {
  form: IForm;
  setForm: (
    key: KeyType,
    value: string | Record<string, string | boolean | number>,
  ) => void;
  onSaveLoading: boolean;
  onSaveSettings: () => void;
  onReset: () => void;
}

const Templates = [
  {
    id: '1',
    url: 'https://res.cloudinary.com/media-resource/image/upload/v1738344070/shipaid-dev/b6exk2iylvk2co2zvaim.png',
    name: 'Template A',
    description: 'Clean, modern layout',
  },
  {
    id: '2',
    url: 'http://res.cloudinary.com/media-resource/image/upload/v1738344113/shipaid-dev/dq801wznalhgmtlojdsj.png',
    name: 'Template B',
    description: 'Minimal neutral layout',
  },
  {
    id: '3',
    url: 'http://res.cloudinary.com/media-resource/image/upload/v1738344140/shipaid-dev/v850wsuinmsszzdb9gom.png',
    name: 'Template C',
    description: 'Professional layout',
  },
];

// Group labels into logical sections
const groupLabels = (labels: Record<string, string>) => {
  const groups: Record<string, Array<{ key: string; label: string }>> = {
    'Intro Section': [
      { key: 'homePageHeader', label: labels.homePageHeader || '' },
      { key: 'homePageDescription', label: labels.homePageDescription || '' },
    ],
    'Form Labels': [
      { key: 'emailPlaceholder', label: labels.emailPlaceholder || '' },
      {
        key: 'orderNumberPlaceholder',
        label: labels.orderNumberPlaceholder || '',
      },
      { key: 'searchButtonText', label: labels.searchButtonText || '' },
      {
        key: 'newDeliveryIssueTitleLabel',
        label: labels.newDeliveryIssueTitleLabel || '',
      },
      {
        key: 'newDeliveryIssueDescLabel',
        label: labels.newDeliveryIssueDescLabel || '',
      },
    ],
    'Items Section': [
      { key: 'itemsHeadingLabel', label: labels.itemsHeadingLabel || '' },
      {
        key: 'selectIssueItemsLabel',
        label: labels.selectIssueItemsLabel || '',
      },
      {
        key: 'selectAtleastOneItemErrorLabel',
        label: labels.selectAtleastOneItemErrorLabel || '',
      },
    ],
    'Cause Section': [
      { key: 'causeHeadingLabel', label: labels.causeHeadingLabel || '' },
      { key: 'selectCauseDescLabel', label: labels.selectCauseDescLabel || '' },
      {
        key: 'selectIssueCauseLabel',
        label: labels.selectIssueCauseLabel || '',
      },
    ],
    'Uploads (Images & Videos)': [
      {
        key: 'uploadImagesHeadingLabel',
        label: labels.uploadImagesHeadingLabel || '',
      },
      {
        key: 'uploadImagesDescLabel',
        label: labels.uploadImagesDescLabel || '',
      },
      { key: 'uploadImagesMaxLabel', label: labels.uploadImagesMaxLabel || '' },
      { key: 'uploadProgressLabel', label: labels.uploadProgressLabel || '' },
      {
        key: 'uploadAtleastOneImageLabel',
        label: labels.uploadAtleastOneImageLabel || '',
      },
      {
        key: 'uploadVideosHeadingLabel',
        label: labels.uploadVideosHeadingLabel || '',
      },
      {
        key: 'uploadVideosDescLabel',
        label: labels.uploadVideosDescLabel || '',
      },
      { key: 'uploadVideosMaxLabel', label: labels.uploadVideosMaxLabel || '' },
      {
        key: 'uploadAtleastOneVideoLabel',
        label: labels.uploadAtleastOneVideoLabel || '',
      },
    ],
    'Preferred Resolution': [
      {
        key: 'preferredSolutionHeadingLabel',
        label: labels.preferredSolutionHeadingLabel || '',
      },
      {
        key: 'preferredSolutionDescLabel',
        label: labels.preferredSolutionDescLabel || '',
      },
      {
        key: 'preferredSolutionOtherLabel',
        label: labels.preferredSolutionOtherLabel || '',
      },
      {
        key: 'preferredSolutionOtherTextAreaLabel',
        label: labels.preferredSolutionOtherTextAreaLabel || '',
      },
    ],
    'Review Issue': [
      {
        key: 'reviewIssueHeadingLabel',
        label: labels.reviewIssueHeadingLabel || '',
      },
      { key: 'reviewIssueDescLabel', label: labels.reviewIssueDescLabel || '' },
      {
        key: 'reviewIssueButtonLabel',
        label: labels.reviewIssueButtonLabel || '',
      },
      { key: 'imagesHeadingLabel', label: labels.imagesHeadingLabel || '' },
      { key: 'editIssueLabel', label: labels.editIssueLabel || '' },
      {
        key: 'submitIssueButtonLabel',
        label: labels.submitIssueButtonLabel || '',
      },
    ],
    'Confirmation Messages': [
      {
        key: 'issueFiledHeadingLabel',
        label: labels.issueFiledHeadingLabel || '',
      },
      { key: 'claimInfoLabel', label: labels.claimInfoLabel || '' },
      { key: 'claimInfoDescLabel', label: labels.claimInfoDescLabel || '' },
      {
        key: 'claimInfoContentLabel',
        label: labels.claimInfoContentLabel || '',
      },
    ],
    'Contact Section': [
      { key: 'contactLabel', label: labels.contactLabel || '' },
      {
        key: 'contactDeclarationLabel',
        label: labels.contactDeclarationLabel || '',
      },
      { key: 'messageSentLabel', label: labels.messageSentLabel || '' },
      { key: 'sendMessageBtnLabel', label: labels.sendMessageBtnLabel || '' },
    ],
    'Address Change Forms': [
      {
        key: 'changeShippingAddressLabel',
        label: labels.changeShippingAddressLabel || '',
      },
      { key: 'addressLabel', label: labels.addressLabel || '' },
      {
        key: 'addressPlaceholderLabel',
        label: labels.addressPlaceholderLabel || '',
      },
      { key: 'address2Label', label: labels.address2Label || '' },
      {
        key: 'address2PlaceholderLabel',
        label: labels.address2PlaceholderLabel || '',
      },
      { key: 'cityLabel', label: labels.cityLabel || '' },
      { key: 'cityPlaceholderLabel', label: labels.cityPlaceholderLabel || '' },
      { key: 'stateLabel', label: labels.stateLabel || '' },
      {
        key: 'statePlaceholderLabel',
        label: labels.statePlaceholderLabel || '',
      },
      { key: 'zipLabel', label: labels.zipLabel || '' },
      { key: 'zipPlaceholderLabel', label: labels.zipPlaceholderLabel || '' },
      { key: 'countryLabel', label: labels.countryLabel || '' },
      {
        key: 'countryPlaceholderLabel',
        label: labels.countryPlaceholderLabel || '',
      },
      {
        key: 'changeAddressButtonLabel',
        label: labels.changeAddressButtonLabel || '',
      },
    ],
    'Order Details': [
      { key: 'orderNumberLabel', label: labels.orderNumberLabel || '' },
      { key: 'orderDateLabel', label: labels.orderDateLabel || '' },
      { key: 'orderTotalLabel', label: labels.orderTotalLabel || '' },
      { key: 'emailLabel', label: labels.emailLabel || '' },
      { key: 'phoneLabel', label: labels.phoneLabel || '' },
      { key: 'shippedToLabel', label: labels.shippedToLabel || '' },
      { key: 'trackingLabel', label: labels.trackingLabel || '' },
      { key: 'estDeliveryDateLabel', label: labels.estDeliveryDateLabel || '' },
      { key: 'shippingAddressLabel', label: labels.shippingAddressLabel || '' },
      { key: 'refundPolicyLabel', label: labels.refundPolicyLabel || '' },
      { key: 'downloadLabel', label: labels.downloadLabel || '' },
    ],
    'Notes & Summary': [
      { key: 'notesHeadingLabel', label: labels.notesHeadingLabel || '' },
      { key: 'notesDescLabel', label: labels.notesDescLabel || '' },
      { key: 'itemsLabel', label: labels.itemsLabel || '' },
      { key: 'summaryLabel', label: labels.summaryLabel || '' },
      { key: 'issueNotesLabel', label: labels.issueNotesLabel || '' },
      { key: 'issueTotalLabel', label: labels.issueTotalLabel || '' },
      { key: 'issueImagesLabel', label: labels.issueImagesLabel || '' },
      { key: 'noImagesTextLabel', label: labels.noImagesTextLabel || '' },
      { key: 'videosLabel', label: labels.videosLabel || '' },
      { key: 'noVideosTextLabel', label: labels.noVideosTextLabel || '' },
    ],
  };

  return groups;
};

const CustomizeSettings = ({
  form,
  setForm,
  onSaveLoading,
  onSaveSettings,
  onReset,
}: ICustomizeSettingsProps) => {
  const labelGroups = groupLabels(BasicSetupForm.labels || {});

  return (
    <div className="w-full">
      <div className="space-y-5 pb-6">
        {/* Header Section */}
        <div className="flex items-start justify-between pb-5 border-b border-[#e1e3e5]">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#2563EB] text-white text-xs font-semibold">
                1
              </span>
              <h1 className="text-[28px] font-semibold text-[#202223] leading-tight">
                Customize Portal Appearance
              </h1>
            </div>
            <p className="text-[#6d7175] text-sm max-w-2xl leading-relaxed">
              Design your portal by choosing a template, customizing colors,
              logos, and labels to match your brand. Once you&apos;re satisfied,
              save your settings and proceed to preview.
            </p>
          </div>
          <Button
            variant="contained"
            disabled={onSaveLoading}
            isLoading={onSaveLoading}
            onClick={onSaveSettings}
            className="ml-4 px-4 py-2 bg-[#2563EB] text-white text-sm font-medium rounded-md hover:bg-[#1d4ed8] transition-colors"
          >
            {onSaveLoading ? 'Please wait ...' : 'Save'}
          </Button>
        </div>

        {/* Template Selection */}
        <section className="bg-white rounded-lg border border-[#e1e3e5] p-6">
          <div className="mb-5">
            <h2 className="text-base font-semibold text-[#202223] mb-1">
              Choose Template
            </h2>
            <p className="text-[#6d7175] text-sm">
              Select a template design for your portal
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Templates.map((t) => (
              <div
                key={t.id}
                className={`border rounded-lg p-4 transition-all cursor-pointer ${
                  form.template === t.id
                    ? 'border-[#2563EB] bg-[#eff6ff] ring-2 ring-[#2563EB] ring-opacity-20'
                    : 'border-[#e1e3e5] hover:border-[#c9cccf] bg-white'
                }`}
                onClick={() => setForm('template', t.id)}
              >
                <div
                  className="w-full h-36 rounded-md mb-3 bg-cover bg-center bg-[#f6f6f7] border border-[#e1e3e5]"
                  style={{
                    backgroundImage: `url(${t.url})`,
                  }}
                />
                <div
                  className={`text-sm font-medium mb-1 ${
                    form.template === t.id ? 'text-[#2563EB]' : 'text-[#202223]'
                  }`}
                >
                  {t.name}
                  {form.template === t.id && (
                    <span className="ml-2 text-xs text-[#2563EB]">
                      • Selected
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#6d7175]">{t.description}</p>
              </div>
            ))}
          </div>
        </section>
        {/* Logo Section */}
        <section className="bg-white rounded-lg border border-[#e1e3e5] p-6">
          <h2 className="text-base font-semibold text-[#202223] mb-5">Logo</h2>

          <div className="flex items-center mb-5">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 text-[#2563EB] focus:ring-[#2563EB] border-[#c9cccf] rounded"
                checked={form.logo?.enableLogo ?? true}
                onChange={(e) =>
                  setForm('logo', { enableLogo: e.target.checked })
                }
              />
              <span className="text-sm text-[#202223]">Enable Logo</span>
            </label>
          </div>

          {(form?.logo?.enableLogo ?? true) && (
            <div className="space-y-5 pt-5 border-t border-[#e1e3e5]">
              <div>
                <label className="block text-sm font-medium text-[#202223] mb-2">
                  Image
                </label>
                <ImageUploader
                  image={form.logo?.url || ''}
                  setImage={(url) => setForm('logo', { url })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-[#202223] mb-2">
                    Size
                  </label>
                  <NumberInput
                    value={form.logo?.size || '250'}
                    min={50}
                    max={500}
                    setValue={(size: number | string) =>
                      setForm('logo', { size })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#202223] mb-2">
                    Position
                  </label>
                  <SelectBox
                    options={[
                      { label: 'Left', value: 'start' },
                      { label: 'Center', value: 'center' },
                      { label: 'Right', value: 'end' },
                    ]}
                    selected={form.logo?.position || 'start'}
                    selectOption={(position: string) =>
                      setForm('logo', { position })
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Colors Section */}
        <section className="bg-white rounded-lg border border-[#e1e3e5] p-6">
          <h2 className="text-base font-semibold text-[#202223] mb-5">
            Theme & Colors
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {(
              [
                { key: 'primary', label: 'Primary Color' },
                { key: 'background', label: 'Background Color' },
                { key: 'border', label: 'Border Color' },
                { key: 'text', label: 'Text Color' },
                { key: 'buttonText', label: 'Button Text Color' },
              ] as const
            ).map(({ key, label }) => {
              const themeKey = key as keyof NonNullable<typeof form.theme>;
              return (
                <div key={key}>
                  <label className="block text-sm font-medium text-[#202223] mb-2">
                    {label}
                  </label>
                  <ColorPicker
                    color={(form.theme?.[themeKey] as string) || ''}
                    setColor={(color: string) =>
                      setForm('theme', { [key]: color })
                    }
                  />
                </div>
              );
            })}
          </div>
        </section>
        {/* Background Banner */}
        <section className="bg-white rounded-lg border border-[#e1e3e5] p-6">
          <h2 className="text-base font-semibold text-[#202223] mb-5">
            Backgrounds
          </h2>

          <div className="flex items-center mb-5">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 text-[#2563EB] focus:ring-[#2563EB] border-[#c9cccf] rounded"
                checked={form.backgrounds?.enableBanner ?? true}
                onChange={(e) =>
                  setForm('backgrounds', { enableBanner: e.target.checked })
                }
              />
              <span className="text-sm text-[#202223]">
                Enable HomePage Banner
              </span>
            </label>
          </div>

          {form?.backgrounds?.enableBanner ? (
            <div className="mb-5">
              <label className="block text-sm font-medium text-[#202223] mb-2">
                Background Image
              </label>
              <ImageUploader
                image={form.backgrounds?.homePageBanner || ''}
                setImage={(homePageBanner) =>
                  setForm('backgrounds', { homePageBanner })
                }
              />
            </div>
          ) : (
            <div className="mb-5">
              <label className="block text-sm font-medium text-[#202223] mb-2">
                Background Color
              </label>
              <ColorPicker
                color={form.backgrounds?.homePageBackgroundColor || ''}
                setColor={(homePageBackgroundColor: number | string) =>
                  setForm('backgrounds', { homePageBackgroundColor })
                }
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[#202223] mb-2">
              Background Transparency (0 - 100%)
            </label>
            <NumberInput
              label=""
              min={0}
              step="1"
              max={100}
              value={form.backgrounds?.formOverlayOpacity || ''}
              setValue={(formOverlayOpacity: number | string) =>
                setForm('backgrounds', { formOverlayOpacity })
              }
            />
          </div>
        </section>

        {/* Labels & Translations - Accordion */}
        <section className="bg-white rounded-lg border border-[#e1e3e5] p-6">
          <div className="mb-5">
            <h2 className="text-base font-semibold text-[#202223] mb-2">
              Labels / Translations
            </h2>
            <p className="text-[#6d7175] text-sm max-w-2xl leading-relaxed">
              Customize the text labels shown to customers in the claims portal.
              Each field below shows the{' '}
              <strong className="text-[#202223]">current label value</strong>
              —edit it to change what customers will see.
            </p>
          </div>

          <div className="divide-y border border-[#e1e3e5] rounded-lg overflow-hidden">
            {Object.entries(labelGroups).map(([sectionName, items]) => (
              <details key={sectionName} className="group">
                <summary className="cursor-pointer font-medium text-[#202223] px-4 py-3.5 hover:bg-[#f6f6f7] transition-colors list-none flex items-center justify-between">
                  <span className="text-sm">{sectionName}</span>
                  <svg
                    className="w-4 h-4 text-[#6d7175] transition-transform group-open:rotate-180"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </summary>
                <div className="px-4 pb-5 pt-3 space-y-5 border-t border-[#e1e3e5] bg-[#fafbfb]">
                  {items.map(({ key, label }) => {
                    const currentValue = form.labels?.[key] || label;
                    return (
                      <div key={key} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium text-[#202223]">
                            {label}
                          </label>
                          <span className="text-xs text-[#6d7175] font-mono bg-white px-2 py-0.5 rounded border border-[#e1e3e5]">
                            {key}
                          </span>
                        </div>
                        <div className="relative">
                          <TextInput
                            label=""
                            value={currentValue}
                            setValue={(val) =>
                              setForm('labels', { [key]: val })
                            }
                          />
                          <p className="mt-1.5 text-xs text-[#6d7175] flex items-center gap-1.5">
                            <svg
                              className="w-3.5 h-3.5 text-[#6d7175]"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            Current label value. Edit to change what customers
                            see.
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </details>
            ))}
          </div>
        </section>

        {/* Container Styles */}
        <section className="bg-white rounded-lg border border-[#e1e3e5] p-6">
          <h2 className="text-base font-semibold text-[#202223] mb-5">
            Container Styles
          </h2>
          <div className="mb-5">
            <label className="block text-sm font-medium text-[#202223] mb-2">
              Border Radius
            </label>
            <NumberInput
              label=""
              value={form.containerStyle?.borderRadius || ''}
              setValue={(borderRadius: number | string) =>
                setForm('containerStyle', { borderRadius })
              }
            />
          </div>
        </section>

        {/* Page-level Save Button */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-[#e1e3e5]">
          <Button
            variant="outlined"
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-[#202223] border border-[#c9cccf] rounded-md hover:bg-[#f6f6f7] transition-colors"
          >
            Reset Customization
          </Button>
          <Button
            variant="contained"
            disabled={onSaveLoading}
            isLoading={onSaveLoading}
            onClick={onSaveSettings}
            className="px-4 py-2 bg-[#2563EB] text-white text-sm font-medium rounded-md hover:bg-[#1d4ed8] transition-colors"
          >
            {onSaveLoading ? 'Please wait ...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomizeSettings;
